import { useCallback, useRef, useState } from "react";
import { useRestClientContext } from "../contexts/RestClientContext";
import { Accessor, PropertyAttribute } from "../types/types";
import { useTeamContext } from "../contexts/TeamContext";
import { useDeepCompareEffect } from "use-deep-compare";
import useStateFromQuery from "./useStateFromQuery";
import { DataTableState } from "../components/UI/DataTable/DataTable";
import _ from "lodash";

interface UsePropertyQueryProps {
	accessors: Accessor[];
	propertyAttributes: PropertyAttribute[];
	ids?: number[];
	groupId?: number;
}

export default function usePropertyQuery({ accessors, propertyAttributes, ids, groupId }: UsePropertyQueryProps) {
	const api = useRestClientContext();
	const team = useTeamContext();
	const state = useStateFromQuery<DataTableState>();

	const hasIdFilter = Array.isArray(ids) && ids.length > 0;

	const [queryId, setQueryId] = useState<string | undefined>(() =>
		hasIdFilter ? undefined : (state?.queryId ?? undefined)
	);
	const [loading, setLoading] = useState(() => (hasIdFilter ? true : !state?.queryId));
	const firstRender = useRef({ accessors, propertyAttributes, ids, groupId });

	const syncAccessors = useCallback(
		(next: { accessors: Accessor[]; propertyAttributes: PropertyAttribute[] }) => {
			firstRender.current = {
				accessors: next.accessors,
				propertyAttributes: next.propertyAttributes,
				ids,
				groupId,
			};
		},
		[ids, groupId]
	);

	useDeepCompareEffect(() => {
		// NB: Don't query "new group"
		if (groupId === -1) {
			setLoading(false);
			return;
		}

		const controller = new AbortController();

		const queryIdFromUrlOrState = state?.queryId ?? queryId;

		// Skip creating a new query when we already have an id and accessors still match the first render.
		if (
			!hasIdFilter &&
			_.isEqual(firstRender.current, { accessors, propertyAttributes, ids, groupId }) &&
			queryIdFromUrlOrState
		) {
			return;
		}

		setLoading(true);

		api.v3
			.createPropertyQuery(team, accessors, propertyAttributes, controller.signal, ids, groupId, [
				...propertyAttributes,
				...accessors,
			])
			.then((response) => {
				setQueryId(response.id);
				setLoading(false);
			});

		return () => controller.abort("Query changed");
	}, [accessors, propertyAttributes, ids, groupId]);

	// Create a new query including the arrangement. We don't need to set loading for this as it should be in the
	// background.
	const saveArrangement = useCallback(
		(newArrangement: string[]) => {
			const controller = new AbortController();

			api.v3
				.createPropertyQuery(
					team,
					accessors,
					propertyAttributes,
					controller.signal,
					ids,
					groupId,
					newArrangement as (Accessor | PropertyAttribute)[]
				)
				.then((response) => {
					setQueryId(response.id);
				});
		},
		[api, team, accessors, propertyAttributes, ids, groupId]
	);

	return { loading, queryId, saveArrangement, syncAccessors };
}
